// useSettingsPlanPaneState.js

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Context
import { useBase } from 'context/BaseContext';

// Services
import { getBaseApiLimits } from 'services/api/ApiAccessService';
import {
    getBaseBillingRecord,
    hasActiveBasePaidSubscription,
    subscribeToBaseBillingRecord,
} from 'services/billing/BaseBillingService';
import { listActivePlans } from 'services/plans/PlanService';
import {
    cancelBaseStripeSubscription,
    createBaseBillingPageSession,
    refreshBaseBillingFromStripe,
} from 'services/billing/BaseSubscriptionWriteService';
import { getBaseLiveUsageSnapshot } from 'services/bases/BaseAdminService';
import { updateBasePlanSelection } from 'services/bases';
import { LIVE_ROOMS_SURFACE_ENABLED } from 'features/liveRoomsSurface';

// Utils
import {
    formatSubscriptionCancelScheduledMessage,
    getDefaultFreePlan,
    isSelfServeSelectable,
} from '../utils/settingsPlanPaneUtils';

/**
 * useSettingsPlanPaneState
 *
 * Manages plan catalog loading, billing state, usage snapshots, and billing actions for SettingsPlanPane.
 *
 * @param {object} params - Hook parameters.
 * @param {object} params.user - Active user model.
 * @param {object} params.base - Active base model.
 * @param {boolean} params.canEditBase - Whether the current user can edit space settings.
 * @param {string} [params.preferredPlanKey] - Optional plan key from marketing deep link.
 *
 * @returns {object} Grouped pane state and handlers.
 */
const useSettingsPlanPaneState = ({
    user,
    base,
    canEditBase,
    preferredPlanKey = '',
}) => {
    const { setActiveBase } = useBase();

    const [plans, setPlans] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [savingPlanKey, setSavingPlanKey] = useState('');
    const [billingRecord, setBillingRecord] = useState(null);
    const [billingLoading, setBillingLoading] = useState(false);
    const [apiLimits, setApiLimits] = useState(null);
    const [apiUsageLoading, setApiUsageLoading] = useState(false);
    const [apiUsageError, setApiUsageError] = useState('');
    const [checkoutPlanKey, setCheckoutPlanKey] = useState('');
    const [downgradePlanKey, setDowngradePlanKey] = useState('');
    const [isCancelSubscriptionConfirming, setIsCancelSubscriptionConfirming] = useState(false);
    const [isCancellingSubscription, setIsCancellingSubscription] = useState(false);
    const [isOpeningBillingPage, setIsOpeningBillingPage] = useState(false);
    const billingPeriodEndSyncAttemptRef = useRef('');
    const preferredPlanHandledRef = useRef('');
    const [liveUsage, setLiveUsage] = useState(null);
    const [liveUsageLoading, setLiveUsageLoading] = useState(false);
    const [liveUsageError, setLiveUsageError] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [statusMessage, setStatusMessage] = useState('');

    useEffect(() => {
        let isActive = true;

        const loadPlans = async () => {
            setIsLoading(true);
            setErrorMessage('');

            try {
                const nextPlans = await listActivePlans();
                if (!isActive) return;
                setPlans(nextPlans);
            } catch (error) {
                if (!isActive) return;
                setPlans([]);
                setErrorMessage(error?.message || 'Failed to load space plans.');
            } finally {
                if (!isActive) return;
                setIsLoading(false);
            }
        };

        loadPlans();

        return () => {
            isActive = false;
        };
    }, [base?.key]);

    useEffect(() => {
        const baseKey = String(base?.key || '').trim();
        if (!baseKey || !canEditBase) {
            setBillingRecord(null);
            setBillingLoading(false);
            return undefined;
        }

        setBillingLoading(true);

        const unsubscribe = subscribeToBaseBillingRecord(
            baseKey,
            (nextBillingRecord) => {
                setBillingRecord(nextBillingRecord);
                setBillingLoading(false);
            },
            () => {
                setBillingRecord(null);
                setBillingLoading(false);
            }
        );

        return unsubscribe;
    }, [canEditBase, base?.key]);

    useEffect(() => {
        const baseKey = String(base?.key || '').trim();
        if (!baseKey || !canEditBase || billingLoading) {
            return undefined;
        }

        if (!billingRecord?.cancelAtPeriodEnd || billingRecord?.currentPeriodEnd) {
            return undefined;
        }

        const syncKey = `${baseKey}:${billingRecord?.stripeSubscriptionId || ''}`;
        if (billingPeriodEndSyncAttemptRef.current === syncKey) {
            return undefined;
        }

        billingPeriodEndSyncAttemptRef.current = syncKey;
        let isActive = true;

        const syncBillingPeriodEnd = async () => {
            try {
                await refreshBaseBillingFromStripe({ baseKey });
                if (!isActive) return;

                const nextBillingRecord = await getBaseBillingRecord(baseKey);
                if (isActive) {
                    setBillingRecord(nextBillingRecord);
                }
            } catch (error) {
                if (isActive) {
                    billingPeriodEndSyncAttemptRef.current = '';
                }
            }
        };

        syncBillingPeriodEnd();

        return () => {
            isActive = false;
        };
    }, [
        billingLoading,
        billingRecord?.cancelAtPeriodEnd,
        billingRecord?.currentPeriodEnd,
        billingRecord?.stripeSubscriptionId,
        canEditBase,
        base?.key,
    ]);

    useEffect(() => {
        const baseKey = String(base?.key || '').trim();
        if (!baseKey) {
            setApiLimits(null);
            setApiUsageError('');
            setApiUsageLoading(false);
            return;
        }

        let isActive = true;

        const loadApiLimits = async () => {
            setApiUsageLoading(true);
            setApiUsageError('');

            try {
                const nextApiLimits = await getBaseApiLimits({ baseId: baseKey });
                if (!isActive) return;
                setApiLimits(nextApiLimits || null);
            } catch (error) {
                if (!isActive) return;
                setApiLimits(null);
                setApiUsageError(error?.message || 'Could not load API usage.');
            } finally {
                if (!isActive) return;
                setApiUsageLoading(false);
            }
        };

        loadApiLimits();

        return () => {
            isActive = false;
        };
    }, [base?.key]);

    useEffect(() => {
        // Skip live usage fetch while the product surface is gated off.
        if (!LIVE_ROOMS_SURFACE_ENABLED) {
            setLiveUsage(null);
            setLiveUsageError('');
            setLiveUsageLoading(false);
            return;
        }

        const baseKey = String(base?.key || '').trim();
        if (!baseKey) {
            setLiveUsage(null);
            setLiveUsageError('');
            setLiveUsageLoading(false);
            return;
        }

        let isActive = true;

        const loadLiveUsage = async () => {
            setLiveUsageLoading(true);
            setLiveUsageError('');

            try {
                const nextLiveUsage = await getBaseLiveUsageSnapshot({ baseKey });
                if (!isActive) return;
                setLiveUsage(nextLiveUsage || null);
            } catch (error) {
                if (!isActive) return;
                setLiveUsage(null);
                setLiveUsageError(error?.message || 'Could not load live usage.');
            } finally {
                if (!isActive) return;
                setLiveUsageLoading(false);
            }
        };

        loadLiveUsage();

        return () => {
            isActive = false;
        };
    }, [base?.key]);

    const currentPlanKey = String(base?.planKey || '').trim();

    const currentPlan = useMemo(() => {
        if (!currentPlanKey) return null;
        return plans.find((plan) => String(plan?.key || '').trim() === currentPlanKey) || null;
    }, [currentPlanKey, plans]);

    const currentPlanTitle = String(currentPlan?.title || base?.planTitle || '').trim() || 'No plan assigned';
    const isCurrentPlanHiddenFromCatalog = Boolean(currentPlanKey) && !currentPlan;

    const hasActivePaidSubscription = useMemo(() => {
        return hasActiveBasePaidSubscription(billingRecord);
    }, [billingRecord]);

    const checkoutPlan = useMemo(() => {
        if (!checkoutPlanKey) return null;
        return plans.find((plan) => String(plan?.key || '').trim() === checkoutPlanKey) || null;
    }, [checkoutPlanKey, plans]);

    const billingSubscriptionId = String(billingRecord?.stripeSubscriptionId || '').trim();

    const defaultFreePlan = useMemo(() => {
        return getDefaultFreePlan(plans);
    }, [plans]);

    const canCancelPaidSubscription = Boolean(
        canEditBase
        && billingSubscriptionId
        && hasActivePaidSubscription
        && !billingRecord?.cancelAtPeriodEnd
        && !isCancellingSubscription
        && !savingPlanKey
    );

    const canOpenBillingPage = Boolean(
        canEditBase
        && String(billingRecord?.stripeCustomerId || '').trim()
        && !isCancellingSubscription
        && !savingPlanKey
    );

    /**
     * applyBasePlanAssignment
     *
     * Applies a space plan assignment to the local base context.
     *
     * @param {object} params - Assignment values.
     * @param {string|null} params.planKey - Next space plan key.
     * @param {string|null} params.planTitle - Next space plan title.
     * @returns {void}
     */
    const applyBasePlanAssignment = useCallback(({ planKey, planTitle }) => {
        setActiveBase((previous) => {
            if (!previous || previous.key !== base?.key) return previous;

            return {
                ...previous,
                dateModified: new Date(),
                planAssignedAt: new Date(),
                planAssignedBy: user?.key || null,
                planKey: planKey || null,
                planTitle: planTitle || null,
            };
        });
    }, [setActiveBase, user?.key, base?.key]);

    /**
     * handleStartDowngradeToPlan
     *
     * Opens downgrade confirmation for moving from paid billing to a free plan.
     *
     * @param {object} plan - Target free plan row.
     * @returns {void}
     */
    const handleStartDowngradeToPlan = useCallback((plan) => {
        const nextPlanKey = String(plan?.key || '').trim();
        if (!nextPlanKey || !isSelfServeSelectable(plan)) return;

        setCheckoutPlanKey('');
        setDowngradePlanKey(nextPlanKey);
        setErrorMessage('');
        setStatusMessage('');
        setIsCancelSubscriptionConfirming(true);
    }, []);

    /**
     * handleSelectPlan
     *
     * Assigns a self-serve plan to the current base.
     *
     * @param {object} plan - Selected public plan row.
     * @returns {Promise<void>} Resolves when the plan assignment completes.
     */
    const handleSelectPlan = useCallback(async (plan) => {
        const nextPlanKey = String(plan?.key || '').trim();
        const nextPlanTitle = String(plan?.title || '').trim();

        if (savingPlanKey || !base?.key || !nextPlanKey) return;

        if (!isSelfServeSelectable(plan)) {
            setErrorMessage('Paid self-serve plan changes are not enabled yet.');
            setStatusMessage('');
            return;
        }

        if (hasActivePaidSubscription) {
            handleStartDowngradeToPlan(plan);
            return;
        }

        setCheckoutPlanKey('');
        setDowngradePlanKey('');
        setIsCancelSubscriptionConfirming(false);

        setErrorMessage('');
        setStatusMessage('Updating space plan...');
        setSavingPlanKey(nextPlanKey);

        try {
            const result = await updateBasePlanSelection({
                baseKey: base.key,
                planKey: nextPlanKey,
            });

            applyBasePlanAssignment({
                planKey: String(result?.planKey || nextPlanKey).trim(),
                planTitle: String(result?.planTitle || nextPlanTitle).trim(),
            });

            setStatusMessage(`${String(result?.planTitle || nextPlanTitle).trim()} is now active for this space.`);
        } catch (error) {
            setErrorMessage(error?.message || 'Failed to update space plan.');
            setStatusMessage('');
        } finally {
            setSavingPlanKey('');
        }
    }, [
        applyBasePlanAssignment,
        handleStartDowngradeToPlan,
        hasActivePaidSubscription,
        savingPlanKey,
        base?.key,
    ]);

    /**
     * handleStartPaidCheckout
     *
     * Opens checkout modal for a paid plan.
     *
     * @param {object} plan - Selected paid plan row.
     * @returns {void}
     */
    const handleStartPaidCheckout = useCallback((plan) => {
        const nextPlanKey = String(plan?.key || '').trim();
        if (!nextPlanKey) return;

        setErrorMessage('');
        setStatusMessage('');
        setCheckoutPlanKey(nextPlanKey);
        setDowngradePlanKey('');
        setIsCancelSubscriptionConfirming(false);
    }, []);

    // Marketing deep link: open checkout (paid) or assign (free) for preferred plan once.
    useEffect(() => {
        const nextPreferred = String(preferredPlanKey || '').trim();
        if (!nextPreferred || isLoading || !canEditBase) return;
        if (preferredPlanHandledRef.current === nextPreferred) return;
        if (!plans.length) return;

        const targetPlan = plans.find((plan) => String(plan?.key || '').trim() === nextPreferred) || null;
        if (!targetPlan) {
            preferredPlanHandledRef.current = nextPreferred;
            setErrorMessage(`Plan "${nextPreferred}" is not available for self-serve.`);
            return;
        }

        const currentKey = String(base?.planKey || '').trim();
        if (currentKey === nextPreferred) {
            preferredPlanHandledRef.current = nextPreferred;
            setStatusMessage(`${String(targetPlan.title || nextPreferred).trim()} is already active on this base.`);
            return;
        }

        preferredPlanHandledRef.current = nextPreferred;

        if (isSelfServeSelectable(targetPlan)) {
            handleSelectPlan(targetPlan);
            return;
        }

        handleStartPaidCheckout(targetPlan);
    }, [
        canEditBase,
        handleSelectPlan,
        handleStartPaidCheckout,
        isLoading,
        plans,
        preferredPlanKey,
        base?.planKey,
    ]);

    /**
     * handleCancelPaidCheckout
     *
     * Closes paid checkout modal.
     *
     * @returns {void}
     */
    const handleCancelPaidCheckout = useCallback(() => {
        setCheckoutPlanKey('');
        setDowngradePlanKey('');
        setErrorMessage('');
        setStatusMessage('');
    }, []);

    /**
     * handleStartCancelSubscription
     *
     * Opens the subscription cancellation confirmation state.
     *
     * @returns {void}
     */
    const handleStartCancelSubscription = useCallback(() => {
        setCheckoutPlanKey('');
        setDowngradePlanKey('');
        setErrorMessage('');
        setStatusMessage('');
        setIsCancelSubscriptionConfirming(true);
    }, []);

    /**
     * handleAbortCancelSubscription
     *
     * Closes the subscription cancellation confirmation state.
     *
     * @returns {void}
     */
    const handleAbortCancelSubscription = useCallback(() => {
        setIsCancelSubscriptionConfirming(false);
        setDowngradePlanKey('');
        setErrorMessage('');
        setStatusMessage('');
    }, []);

    /**
     * handleCancelSubscription
     *
     * Cancels the active paid subscription for this space.
     *
     * @returns {Promise<void>} Resolves when the cancel flow completes.
     */
    const handleCancelSubscription = useCallback(async () => {
        if (!base?.key || !billingSubscriptionId || isCancellingSubscription) return;

        setCheckoutPlanKey('');
        setErrorMessage('');

        setStatusMessage('Scheduling subscription cancellation...');
        setIsCancelSubscriptionConfirming(false);
        setIsCancellingSubscription(true);

        try {
            const result = await cancelBaseStripeSubscription({
                subscriptionId: billingSubscriptionId,
                baseKey: base.key,
            });

            const nextBillingRecord = await getBaseBillingRecord(base.key);
            setBillingRecord(nextBillingRecord);

            const scheduledCancellation = Boolean(
                result?.cancelAtPeriodEnd
                || nextBillingRecord?.cancelAtPeriodEnd
            );

            if (scheduledCancellation) {
                const activePlanTitle = String(
                    nextBillingRecord?.planTitle
                    || base?.planTitle
                    || result?.effectiveBasePlanTitle
                    || ''
                ).trim();

                setStatusMessage(formatSubscriptionCancelScheduledMessage({
                    billingRecord: nextBillingRecord,
                    planTitle: activePlanTitle,
                }) || 'Subscription cancellation scheduled for the end of the current billing period.');
                setErrorMessage('');
                return;
            }

            const nextPlanKey = String(
                result?.effectiveBasePlanKey
                || defaultFreePlan?.key
                || ''
            ).trim();
            const nextPlanTitle = String(
                result?.effectiveBasePlanTitle
                || defaultFreePlan?.title
                || ''
            ).trim();

            if (nextPlanKey) {
                applyBasePlanAssignment({
                    planKey: nextPlanKey,
                    planTitle: nextPlanTitle,
                });
            }

            if (nextPlanTitle) {
                setStatusMessage(`Subscription canceled. ${nextPlanTitle} is now active for this space.`);
            } else {
                setStatusMessage('Subscription canceled. This space no longer has an assigned plan.');
            }
        } catch (error) {
            setErrorMessage(error?.message || 'Failed to cancel the active base subscription.');
            setStatusMessage('');
        } finally {
            setDowngradePlanKey('');
            setIsCancellingSubscription(false);
        }
    }, [
        applyBasePlanAssignment,
        billingSubscriptionId,
        defaultFreePlan?.key,
        defaultFreePlan?.title,
        isCancellingSubscription,
        base?.planTitle,
        base?.key,
    ]);

    /**
     * handleOpenBillingPage
     *
     * Opens the Stripe billing page for this space in a new tab.
     *
     * @returns {Promise<void>} Resolves when the page flow completes.
     */
    const handleOpenBillingPage = useCallback(async () => {
        if (!base?.key || !canOpenBillingPage || isOpeningBillingPage) return;

        setErrorMessage('');
        setStatusMessage('Opening billing page...');
        setIsOpeningBillingPage(true);

        const pageWindow = window.open('about:blank', '_blank');
        if (!pageWindow) {
            setErrorMessage('Unable to open a new tab. Check your browser popup settings and try again.');
            setStatusMessage('');
            setIsOpeningBillingPage(false);
            return;
        }

        pageWindow.opener = null;

        try {
            const result = await createBaseBillingPageSession({
                returnUrl: window.location.href,
                baseKey: base.key,
            });

            const pageUrl = String(result?.url || '').trim();
            if (!pageUrl) {
                throw new Error('Stripe billing page did not return a URL.');
            }

            pageWindow.location.href = pageUrl;
            setStatusMessage('Billing page opened in a new tab.');

            /**
             * handleWindowFocus
             *
             * Handles window focus.
             * @returns {Promise<void>} Resolves when the handler completes.
             */
            const handleWindowFocus = async () => {
                window.removeEventListener('focus', handleWindowFocus);

                try {
                    await refreshBaseBillingFromStripe({ baseKey: base.key });
                    const nextBillingRecord = await getBaseBillingRecord(base.key);
                    setBillingRecord(nextBillingRecord);
                } catch (refreshError) {
                    // Billing refresh is best-effort after returning from the page.
                }
            };

            window.addEventListener('focus', handleWindowFocus);
        } catch (error) {
            pageWindow.close();
            setErrorMessage(error?.message || 'Failed to open the billing page.');
            setStatusMessage('');
        } finally {
            setIsOpeningBillingPage(false);
        }
    }, [canOpenBillingPage, isOpeningBillingPage, base?.key]);

    /**
     * handlePaidCheckoutSuccess
     *
     * Handles successful paid subscription creation.
     *
     * @param {object} input - Success payload.
     * @param {string} input.billingEmail - Billing email returned by checkout.
     * @param {string} input.billingName - Billing name returned by checkout.
     * @param {object} input.subscriptionResult - Subscription creation result payload.
     * @returns {Promise<void>} Resolves when local billing state is refreshed.
     */
    const handlePaidCheckoutSuccess = useCallback(async ({ billingEmail, billingName, subscriptionResult }) => {
        if (!checkoutPlan || !base?.key) {
            setCheckoutPlanKey('');
            return;
        }

        try {
            const nextBillingRecord = await getBaseBillingRecord(base.key);
            if (!hasActiveBasePaidSubscription(nextBillingRecord)) {
                throw new Error('Subscription payment is not active yet. Please try again or refresh plan settings.');
            }

            const effectivePlanKey = String(
                nextBillingRecord?.planKey
                || subscriptionResult?.effectiveBasePlanKey
                || ''
            ).trim();
            const effectivePlanTitle = String(
                nextBillingRecord?.planTitle
                || subscriptionResult?.effectiveBasePlanTitle
                || ''
            ).trim();

            if (!effectivePlanKey) {
                throw new Error('Subscription is active, but no space plan was assigned.');
            }

            setBillingRecord(nextBillingRecord);

            applyBasePlanAssignment({
                planKey: effectivePlanKey,
                planTitle: effectivePlanTitle,
            });

            setCheckoutPlanKey('');
            setStatusMessage(`${effectivePlanTitle || checkoutPlan.title} billing is now set up for this space.`);
            setErrorMessage('');
        } catch (error) {
            setErrorMessage(error?.message || 'Subscription was created, but the space plan could not be updated.');
            setStatusMessage('');
        }
    }, [applyBasePlanAssignment, checkoutPlan, base?.key]);

    return {
        planState: {
            plans,
            isLoading,
            savingPlanKey,
            currentPlanKey,
            currentPlan,
            currentPlanTitle,
            isCurrentPlanHiddenFromCatalog,
            checkoutPlan,
            downgradePlanKey,
        },
        billingState: {
            billingRecord,
            billingLoading,
            billingSubscriptionId,
            hasActivePaidSubscription,
            isCancelSubscriptionConfirming,
            isCancellingSubscription,
            canCancelPaidSubscription,
            isOpeningBillingPage,
            canOpenBillingPage,
        },
        usageState: {
            apiLimits,
            apiUsageLoading,
            apiUsageError,
            liveUsage,
            liveUsageLoading,
            liveUsageError,
        },
        messageState: {
            errorMessage,
            statusMessage,
        },
        handlers: {
            handleSelectPlan,
            handleStartPaidCheckout,
            handleCancelPaidCheckout,
            handleStartCancelSubscription,
            handleStartDowngradeToPlan,
            handleAbortCancelSubscription,
            handleCancelSubscription,
            handleOpenBillingPage,
            handlePaidCheckoutSuccess,
        },
    };
};

export default useSettingsPlanPaneState;